export class BaseWebSocketClient {
  constructor(assignmentId, alpineComponent = null) {
    this.assignmentId = assignmentId
    this.alpineComponent = alpineComponent
    this.socket = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000
    this.heartbeatInterval = null
    this.heartbeatTimeout = null
    this.heartbeatIntervalMs = 30000
    this.heartbeatTimeoutMs = 10000
    this.connect()
  }

  // Abstract method - must be implemented by subclasses
  getWebSocketUrl() {
    throw new Error('getWebSocketUrl must be implemented by subclass')
  }

  connect() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const isDevelopment =
      window.location.hostname === 'localhost' &&
      window.location.port === '3002'
    const isLocalIP = /^(?:192\.168|10\.|172\.(?:1[6-9]|2[0-9]|3[0-1])\.)/.test(
      window.location.hostname
    )
    const isStudentLocal = window.location.hostname === 'student.local.org'

    let wsHost
    if (isStudentLocal) {
      // For reverse proxy setup, use the same host
      wsHost = window.location.host
    } else if (isDevelopment || isLocalIP) {
      wsHost = `${window.location.hostname}:4000`
    } else {
      wsHost = window.location.host
    }

    // Get the specific path from the subclass
    const wsPath = this.getWebSocketUrl()
    const wsUrl = `${protocol}//${wsHost}${wsPath}`

    try {
      this.socket = new WebSocket(wsUrl)
      this.socket.onopen = () => this.onOpen()
      this.socket.onmessage = (event) => this.onMessage(JSON.parse(event.data))
      this.socket.onclose = (event) => this.onClose(event)
      this.socket.onerror = () => {} // Handled by onclose
    } catch (error) {
      this.attemptReconnect()
    }
  }

  onOpen() {
    this.reconnectAttempts = 0
    this.reconnectDelay = 1000
    this.startHeartbeat()
  }

  // Abstract method - must be implemented by subclasses
  onMessage(data) {
    throw new Error('onMessage must be implemented by subclass')
  }

  onClose(event) {
    this.stopHeartbeat()
    const closeCode = event?.code || 0

    switch (closeCode) {
      case 4001:
        console.error('WebSocket connection failed: Channel layer unavailable')
        this.attemptReconnect()
        break
      case 4002:
        console.error('WebSocket connection failed: Authentication required')
        window.location.href = '/login/'
        return
      case 4003:
        console.error(
          'WebSocket connection failed: Access denied to this assignment'
        )
        return
      case 4000:
        console.error('WebSocket connection failed: General error')
        this.attemptReconnect()
        break
      default:
        console.log('WebSocket connection closed:', closeCode)
        this.attemptReconnect()
    }
  }

  startHeartbeat() {
    this.stopHeartbeat()

    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(
          JSON.stringify({
            type: 'ping',
            timestamp: Date.now(),
          })
        )
        this.heartbeatTimeout = setTimeout(() => {
          console.warn('WebSocket heartbeat timeout - no pong received')
          this.socket.close()
        }, this.heartbeatTimeoutMs)
      }
    }, this.heartbeatIntervalMs)
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout)
      this.heartbeatTimeout = null
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      setTimeout(() => this.connect(), this.reconnectDelay)
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000)
    }
  }

  disconnect() {
    this.stopHeartbeat()
    this.socket?.close()
  }

  // Helper method for common pong handling
  handlePong() {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout)
      this.heartbeatTimeout = null
    }
  }
}
